import useCurrentMember from '@/member/hooks/useCurrentMember'
import useOrgMember from '@/member/hooks/useOrgMember'
import useOrgOwner from '@/member/hooks/useOrgOwner'
import { useOrgContext } from '@/org/contexts/OrgContext'
import { CirclePermissions, OrgData } from '@rolebase/shared/model/OrgData'
import { useMemo } from 'react'

export const noCirclePermissions: CirclePermissions = {
  canEditCircle: false,
  canEditRole: false,
  canEditRoleWorkFields: false,
  canEditMembers: false,
  canEditSubCircles: false,
  canEditSubCirclesParentLinks: false,
}

export interface CirclePermissionsInputs {
  orgData?: OrgData
  memberId?: string
  isOrgMember: boolean
  isOrgOwner: boolean
  editable: boolean
}

// Permissions of one circle, as a plain function: a caller resolving several
// circles at once (the parent-link roles of a sub-circle) can't call a hook per
// circle. The rules themselves stay in OrgData.getCirclePermissions, the single
// source of truth mirrored by the Hasura permissions — it re-derives
// leader/owner from the member id, so it needs no React context. Like
// CircleProvider, we only gate the result with `editable`, false in read-only
// contexts (preview, share, draft).
export function getCirclePermissionsOf(
  circleId: string | undefined,
  {
    orgData,
    memberId,
    isOrgMember,
    isOrgOwner,
    editable,
  }: CirclePermissionsInputs
): CirclePermissions {
  const circle = circleId ? orgData?.getCircle(circleId) : undefined
  const role = orgData?.getRole(circle?.roleId)
  if (!circle || !role || !orgData) return noCirclePermissions

  const perms = orgData.getCirclePermissions(
    circle,
    role,
    memberId,
    isOrgMember,
    isOrgOwner
  )

  return {
    canEditCircle: editable && perms.canEditCircle,
    canEditRole: editable && perms.canEditRole,
    canEditRoleWorkFields: editable && perms.canEditRoleWorkFields,
    canEditMembers: editable && perms.canEditMembers,
    canEditSubCircles: editable && perms.canEditSubCircles,
    canEditSubCirclesParentLinks:
      editable && perms.canEditSubCirclesParentLinks,
  }
}

// The inputs getCirclePermissionsOf needs, gathered once per component.
export function useCirclePermissionsInputs(): CirclePermissionsInputs {
  const { editable, orgData } = useOrgContext()
  const currentMember = useCurrentMember()
  const isOrgMember = useOrgMember()
  const isOrgOwner = useOrgOwner()
  return useMemo(
    () => ({
      orgData,
      memberId: currentMember?.id,
      isOrgMember,
      isOrgOwner,
      editable,
    }),
    [orgData, currentMember?.id, isOrgMember, isOrgOwner, editable]
  )
}

// Permissions of any circle, not just the one the surrounding CircleContext
// wraps: a list row often needs the rights of another circle, and nesting a
// CircleProvider per row to read them would be absurd.
export default function useCirclePermissions(
  circleId?: string
): CirclePermissions {
  const inputs = useCirclePermissionsInputs()
  return useMemo(
    () => getCirclePermissionsOf(circleId, inputs),
    [circleId, inputs]
  )
}
